import { Service } from '@toeverything/infra';
import { applyUpdate } from 'yjs';

import { transformWorkspaceDBLocalToCloud } from '../../db/utils';
import type { Workspace } from '../entities/workspace';
import type { WorkspaceMetadata } from '../metadata';
import type { WorkspaceFactoryService } from './factory';

export class WorkspaceTransformService extends Service {
  constructor(private readonly factory: WorkspaceFactoryService) {
    super();
  }

  /**
   * helper function to transform local workspace to cloud workspace
   *
   * @param accountId - all local user data will be transformed to this account
   */
  transformLocalToCloud = async (
    local: Workspace,
    accountId: string,
    flavour: string
  ): Promise<WorkspaceMetadata> => {
    if (local.flavour !== 'local') {
      throw new Error(
        'Only local workspace can be transformed to cloud workspace'
      );
    }

    const localDocStorage = local.engine.doc.storage;
    const localDocList = Array.from(local.docCollection.docs.keys());

    const newMetadata = await this.factory.create(
      flavour,
      async (docCollection, blobStorage, docStorage) => {
        const rootDocBinary = (
          await localDocStorage.getDoc(local.docCollection.doc.guid)
        )?.bin;

        // sl-856: Apply root doc with error handling
        if (rootDocBinary) {
          try {
            applyUpdate(docCollection.doc, rootDocBinary);
          } catch (err) {
            console.error(
              `[WorkspaceTransform] Failed to apply root doc update (corrupt data?):`,
              err
            );
            // Continue with transformation - partial data is better than none
          }
        }

        // sl-856: Apply subdocs with error handling (continue on individual failures)
        for (const subdocId of localDocList) {
          const subdocBinary = (await localDocStorage.getDoc(subdocId))?.bin;
          if (subdocBinary) {
            const doc = docCollection.getDoc(subdocId);
            if (doc) {
              const spaceDoc = doc.spaceDoc;
              doc.load();
              try {
                applyUpdate(spaceDoc, subdocBinary);
              } catch (err) {
                console.error(
                  `[WorkspaceTransform] Failed to apply subdoc ${subdocId} (corrupt data?):`,
                  err
                );
                // Continue with other subdocs - don't fail entire transformation
              }
            }
          }
        }

        // transform db
        await transformWorkspaceDBLocalToCloud(
          local.id,
          docCollection.id,
          localDocStorage,
          docStorage,
          accountId
        );

        const blobList = await local.engine.blob.storage.list();

        for (const { key } of blobList) {
          const blob = await local.engine.blob.storage.get(key);
          if (blob) {
            await blobStorage.set(blob);
          }
        }
      }
    );

    // NOTE: We intentionally do NOT delete the old workspace here.
    // The caller should navigate to the new workspace first, then delete the old one.
    // This prevents a race condition where React detects the deleted workspace
    // before navigation completes, causing SharePage errors.

    return newMetadata;
  };
}
